/**
 * 66.8, digging in (decision 160; ARCHITECTURE §5.18), with GameConfig.entrench. A unit that wants it (UnitIntent.dig) and
 * stands still on the ground digs a scrape where it stands, facing its heading: the scrape deepens by the ground's material
 * (ENTRENCH's rate; rock and ice refuse it, core/dig.ts) and joins its planet's digs from its first quarter, in quarter
 * steps — so a planet takes at most one new `digs` array a tick, however many diggers cross a quarter in it, and the caches
 * keyed on it take their patched path a few times a dig, never every tick. A step, a seat, the air, a hit, its intent's
 * end or its death stops it; dug through, or stopped part-dug, the dig is recorded in the terrain edit log (digEdit), which
 * replays it. Digging again where a dig stands carries that dig on (its depth stood); a scrape dug through is deepened
 * into a foxhole (lane E: the same hole with more time, ENTRENCH.foxholeSeconds), and a foxhole dug through needs no more. `PlayerState.digAt` carries it meanwhile (lane E's pose reads it). Pure.
 */
import type { Dig, GameState, PlanetState, PlayerState, TerrainEdit, UnitIntent, Vec3 } from '../types';
import { ENTRENCH } from '../constants/action';
import { vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { DIG_GROUND, DIGS } from './dig';
import { groundMaterial } from './groundMaterial';
import { headingOf } from './surfaceWalk';
import { digEdit } from './terrainEdits';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dacos } from './dmath';

/** A digger standing within this (person heights) of a dig's centre is in it: it carries that dig on rather than begin another. */
const DIG_SAME_PEOPLE = 0.5;
const arcOn = (a: Vec3, b: Vec3, r: number) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * r;

/** A foxhole begun from a scrape dug through: the share of its depth that covers what the scrape did (DIGS' covers). */
const FOXHOLE_FROM = DIGS.scrape.cover / DIGS.foxhole.cover;

/** A dig's progress in quarters: the depth the planet carries (0, 0.25, 0.5, 0.75, 1). */
const quarter = (progress: number) => Math.min(1, Math.floor(progress * 4 + 1e-9) / 4);

/** One tick (`dt` s, at `seconds` of action time) of digging for every unit, `intents` by player index. The same state when nothing digs. */
export function stepDigs(state: GameState, intents: ReadonlyArray<UnitIntent | undefined>, dt: number, seconds: number): GameState {
  // (a dig is person-scale ground: none where people stand on the mesh — the classic scale, or the relief off)
  if (!state.config.entrench || !(personSetOf(state).fineAmplitude > 0)) return state;
  let players: PlayerState[] | null = null;
  const digs = new Map<number, Dig[]>();   // planet index → its digs this tick (one new array a planet)
  const edits: TerrainEdit[] = [];
  let seq = state.digSeq ?? 0;
  const digsOf = (pi: number): Dig[] => digs.get(pi) ?? state.planets[pi]?.digs ?? [];
  const put = (pi: number, dig: Dig) => { digs.set(pi, [...digsOf(pi).filter(d => d.id !== dig.id), dig]); };
  const set = (i: number, u: PlayerState) => { players ??= state.players.slice(); players[i] = u; };
  const end = (i: number, u: PlayerState) => {
    const at = u.digAt!;
    // (logged when it changed the ground: a deepening stopped before its foxhole passed the scrape changed nothing)
    if (at.dig.done > 0 && !(at.deepen && at.dig.kind === 'scrape')) edits.push(digEdit(u.planetIndex, state.planets[u.planetIndex], at.dig));
    const { digAt: _ended, ...rest } = u;
    set(i, rest as PlayerState);
  };
  for (let i = 0; i < state.players.length; i++) {
    const u = state.players[i];
    const intent = intents[i];
    const still = !!intent && Math.abs(intent.forward) < 1e-3 && Math.abs(intent.right) < 1e-3;
    const can = u.alive && !!intent?.dig && still && !u.airborne && !u.mount && state.planets[u.planetIndex]?.type === 'planet';
    if (!can) { if (u.digAt) end(i, u); continue; }
    if (u.digAt && u.health < u.digAt.health) { end(i, u); continue; }   // (hit: down into the hole, not digging)
    let at = u.digAt;
    if (!at) {
      const dir = vecNormalize(u.surfaceDir);
      // standing in a dig already (its own, stopped part-dug, or a mate's): it carries that one on — or, dug through, has
      // nothing to dig; else a new scrape at its feet, its front the way it faces, where the ground lets it be dug
      const planet = state.planets[u.planetIndex];
      const here = digsOf(u.planetIndex).find(d => arcOn(d.dir, dir, planet.radius) <= DIG_SAME_PEOPLE * personSetOf(state).k * PERSON_BODY_HEIGHT);
      if (here) {
        if (here.kind === 'foxhole' && here.done >= 1) continue;   // (dug as deep as it goes)
        // a scrape dug through is deepened into a foxhole (lane E: the same hole with more time) — begun where a foxhole
        // already covers what the scrape did, so the cover never dips; the planet keeps the scrape until the foxhole passes it
        at = here.kind === 'foxhole' || here.done >= 1
          ? { dig: here, progress: here.kind === 'foxhole' ? here.done : 0, health: u.health, deepen: true }
          : { dig: here, progress: here.done, health: u.health };
      } else {
        const material = groundMaterial(state, u.planetIndex, u.surfaceDir, seconds);
        if (!DIG_GROUND[material] || !(ENTRENCH.rate[material] > 0)) continue;
        at = { dig: { id: seq++, kind: 'scrape', material, dir, facing: tangentFacing(u, dir), done: 0 }, progress: 0, health: u.health };
      }
    }
    if (at.deepen) {
      // deepening: the foxhole's own progress, from where it covers what the scrape did; on the planet once a quarter passes it
      const progress = Math.min(1, Math.max(FOXHOLE_FROM, at.progress) + (dt * ENTRENCH.rate[at.dig.material]) / ENTRENCH.foxholeSeconds);
      const q = quarter(progress);
      let dig = at.dig;
      if (q > FOXHOLE_FROM && (dig.kind !== 'foxhole' || q > dig.done)) { dig = { ...dig, kind: 'foxhole', done: q }; put(u.planetIndex, dig); }
      const next = { ...u, digAt: { dig, progress, health: at.health, deepen: true as const } };
      if (progress >= 1) end(i, next); else set(i, next);
      continue;
    }
    const progress = Math.min(1, at.progress + (dt * ENTRENCH.rate[at.dig.material]) / ENTRENCH.scrapeSeconds);
    const q = quarter(progress);
    let dig = at.dig;
    if (q > dig.done) { dig = { ...dig, done: q }; put(u.planetIndex, dig); }
    const next = { ...u, digAt: { dig, progress, health: at.health } };
    if (progress >= 1) end(i, next); else set(i, next);
  }
  if (!players && digs.size === 0 && edits.length === 0) return state;
  let planets: PlanetState[] = state.planets;
  if (digs.size) { planets = planets.slice(); for (const [pi, list] of digs) planets[pi] = { ...planets[pi], digs: list }; }
  return {
    ...state,
    players: players ?? state.players,
    planets,
    ...(edits.length ? { terrainEdits: [...(state.terrainEdits ?? []), ...edits] } : {}),
    ...(seq !== (state.digSeq ?? 0) ? { digSeq: seq } : {}),
  };
}

/** The tangent the unit faces at `dir` (its heading, laid flat). */
function tangentFacing(u: PlayerState, dir: Vec3): Vec3 {
  const h = headingOf(u);
  const t = vecSub(h, vecScale(dir, vecDot(h, dir)));
  return vecLength(t) > 1e-12 ? vecNormalize(t) : h;
}
